import { useRef, useState } from 'react';
import {
  Clock,
  CalendarDays,
  Repeat,
  Loader2,
  Play,
  Pause,
  Upload,
  Music,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { API_BASE_URL, useGetProgramQuery, useUploadTrackMutation, useUpdateProgramMutation, useListTracksQuery } from '../../store/api';
import { format } from 'date-fns';
import { parseApiDate } from '../utils/dateTime';
import { toast } from 'sonner';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';

interface ProgramDetailPageProps {
  programId: string | number;
  channelId: string | number;
  onBack: () => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function ProgramDetailPage({ programId, channelId, onBack }: ProgramDetailPageProps) {
  const { data: programData, isLoading, isError, refetch } = useGetProgramQuery(Number(programId));
  const [uploadTrack] = useUploadTrackMutation();
  const [updateProgram] = useUpdateProgramMutation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Fetch existing tracks for the channel (only when no track is attached)
  const hasTrack = !!programData?.trackId;
  const { data: tracksRaw = [] } = useListTracksQuery(Number(channelId), { skip: hasTrack });
  const existingTracks = (tracksRaw as any[]).map((t: any) => ({
    id: t.id,
    fileName: t.fileName ?? t.file_name,
    duration: t.duration,
  }));

  if (isLoading) {
    return (
      <div className="p-6 sm:p-8 flex items-center justify-center py-24">
        <Loader2 className="size-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (isError || !programData) {
    return (
      <div className="p-6 sm:p-8 max-w-3xl mx-auto">
        <button onClick={onBack} className="text-sm text-blue-400 hover:text-blue-300 mb-4">
          ← Back to Events
        </button>
        <p className="text-gray-400">Program not found.</p>
      </div>
    );
  }

  const program = {
    ...programData,
    startDateTime: programData.startDateTime,
    endDateTime: programData.endDateTime,
    scheduleType: programData.scheduleType ?? programData.schedule_type,
    recurrence: programData.recurrence,
    description: programData.description,
    thumbnail: programData.thumbnail,
  };

  const start = parseApiDate(program.startDateTime);
  const end = parseApiDate(program.endDateTime);

  const thumbnailUrl = program.thumbnail
    ? program.thumbnail.startsWith('/')
      ? `${API_BASE_URL}${program.thumbnail}`
      : program.thumbnail
    : null;

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              className="text-gray-400 hover:text-white cursor-pointer"
              onClick={onBack}
            >
              Events
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="text-gray-500" />
          <BreadcrumbItem>
            <BreadcrumbPage className="text-white">{program.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Cover image with title overlay */}
      {thumbnailUrl ? (
        <div className="relative rounded-xl overflow-hidden mb-6">
          <img
            src={thumbnailUrl}
            alt={program.title}
            loading="lazy"
            className="w-full h-64 sm:h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">
              {program.title}
            </h1>
          </div>
        </div>
      ) : (
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
          {program.title}
        </h1>
      )}

      {/* Date & Time */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="inline-flex items-center gap-2 text-sm text-gray-200 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
          <CalendarDays className="size-4 text-blue-400" />
          {format(start, 'EEEE, MMM dd, yyyy')}
        </span>
        <span className="inline-flex items-center gap-2 text-sm text-gray-200 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
          <Clock className="size-4 text-blue-400" />
          {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
        </span>
      </div>

      {/* Description */}
      {program.description && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-2">Description</h2>
          <p className="text-gray-300 leading-relaxed whitespace-pre-line">
            {program.description}
          </p>
        </div>
      )}

      {/* Schedule Info */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-6">
        <h2 className="text-lg font-semibold text-white mb-3">Schedule</h2>
        <div className="space-y-2 text-sm text-gray-300">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 w-28 shrink-0">Type:</span>
            <span className="capitalize">{program.scheduleType}</span>
          </div>
          {program.scheduleType === 'recurring' && program.recurrence && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 w-28 shrink-0">Frequency:</span>
                <span className="inline-flex items-center gap-1.5">
                  <Repeat className="size-3.5 text-purple-400" />
                  <span className="capitalize">{program.recurrence.frequency}</span>
                </span>
              </div>
              {program.recurrence.frequency === 'weekly' &&
                program.recurrence.daysOfWeek &&
                program.recurrence.daysOfWeek.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 w-28 shrink-0">Repeat on:</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {program.recurrence.daysOfWeek
                        .slice()
                        .sort((a: number, b: number) => a - b)
                        .map((d: number) => (
                          <span
                            key={d}
                            className="text-xs bg-purple-500/15 text-purple-300 border border-purple-500/25 rounded-md px-2 py-0.5"
                          >
                            {DAY_NAMES[d]}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              {program.recurrence.endDate && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 w-28 shrink-0">Until:</span>
                  <span>{format(parseApiDate(program.recurrence.endDate), 'MMM dd, yyyy')}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Audio Preview / Upload */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-6">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Music className="size-5 text-blue-400" />
          Audio
        </h2>

        {program.trackId ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="ghost"
                className="size-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white p-0"
                onClick={async () => {
                  if (!audioRef.current) {
                    const audio = new Audio();
                    audio.addEventListener('ended', () => setIsPlaying(false));
                    audio.addEventListener('error', () => {
                      setIsPlaying(false);
                      toast.error('Audio file not available for preview');
                    });
                    audioRef.current = audio;
                  }
                  if (isPlaying) {
                    audioRef.current.pause();
                    setIsPlaying(false);
                  } else {
                    try {
                      const res = await fetch(`${API_BASE_URL}/tracks/${program.trackId}/preview`, {
                        credentials: 'include',
                      });
                      if (!res.ok) throw new Error('Failed to fetch audio');
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      audioRef.current.src = url;
                      await audioRef.current.play();
                      setIsPlaying(true);
                    } catch {
                      setIsPlaying(false);
                      toast.error('Audio file not available for preview');
                    }
                  }
                }}
              >
                {isPlaying ? <Pause className="size-4" /> : <Play className="size-4 ml-0.5" />}
              </Button>
              <div>
                <p className="text-sm text-gray-200">Preview (up to 1 min)</p>
                <p className="text-xs text-gray-500">Click play to listen</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-400 mb-4">No audio file attached to this program</p>

            {/* Assign existing track */}
            {existingTracks.length > 0 && (
              <div className="mb-4 max-w-xs mx-auto">
                <p className="text-sm text-gray-300 mb-2">Assign existing file</p>
                <Select
                  disabled={isAssigning}
                  onValueChange={async (trackId) => {
                    setIsAssigning(true);
                    try {
                      await updateProgram({
                        programId: Number(programId),
                        payload: { trackId: Number(trackId) },
                      }).unwrap();
                      toast.success('Audio file assigned');
                      refetch();
                    } catch (err: any) {
                      toast.error(err?.data?.detail || 'Failed to assign file');
                    } finally {
                      setIsAssigning(false);
                    }
                  }}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-gray-200">
                    <SelectValue placeholder="Select a file…" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {existingTracks.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)} className="text-gray-200 focus:bg-gray-700">
                        {t.fileName} ({t.duration})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-3 justify-center mb-3">
              <div className="h-px flex-1 max-w-[60px] bg-gray-700" />
              <span className="text-xs text-gray-500">or upload new</span>
              <div className="h-px flex-1 max-w-[60px] bg-gray-700" />
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,audio/mpeg"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setIsUploading(true);
                try {
                  const trackResult = await uploadTrack({
                    channelId: Number(channelId),
                    file,
                  }).unwrap();
                  await updateProgram({
                    programId: Number(programId),
                    payload: { trackId: trackResult.id },
                  }).unwrap();
                  toast.success('Audio file uploaded and linked');
                  refetch();
                } catch (err: any) {
                  toast.error(err?.data?.detail || 'Upload failed');
                } finally {
                  setIsUploading(false);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }
              }}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isUploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
              {isUploading ? 'Uploading…' : 'Upload Audio File'}
            </Button>
            <p className="text-xs text-gray-500 mt-2">MP3 only · 48 kHz · 128 kbps</p>
          </div>
        )}
      </div>
    </div>
  );
}
